import React, { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router'
import _ from 'lodash'
import moment from 'moment-timezone'

// graphql
import gql from 'graphql-tag'
import { useMutation, useLazyQuery } from '@apollo/react-hooks'

// semantic-ui
import {
  Button,
  Form,
  Header,
  Icon,
  Container,
  Message,
  Modal,
  Loader
} from 'semantic-ui-react'

// styles
import './UpsertSensor.css'

// graphql mutations
const CREATE_SENSOR = gql`
mutation CreateSensorMutation($input: CreateSensorInput!) {
  create_sensor(input: $input) {
    id
    serial
  }
}
`
const UPDATE_SENSOR = gql`
mutation UpdateSensorMutation($input: UpdateSensorInput!) {
  update_sensor(input: $input) {
    id
    serial
  }
}
`
const DELETE_SENSOR = gql`
  mutation DeleteSensor ($input: DeleteSensorInput!) {
    delete_sensor (input: $input) {
      id
    }
  }
`
// graphql query
const SENSORS = gql`
query GetSensors {
  me {
    id
    sensors {
      id
      serial
      model
      license
      po_number
      purchased_at
      created_at
    }
  }
}
`

// model array
const sensorModels = [
  'orbbec-astra-pro'
]

// page component
function UpsertSensor (props)  {
  // use params "sensor" as "sensorId"
  let { sensor: sensorId } = useParams()
  // create/edit mode
  let [mode, setMode] = useState('create')
  // list of sensors in DB
  let [sensors, setSensors] = useState([])
  // input states
  let [po, setPo] = useState('')
  let [purchased, setPurchased] = useState('')
  let [serial, setSerial] = useState('')
  let [model, setModel] = useState(sensorModels[0])
  let [license, setLicense] = useState(null)
  let [createdAt, setCreatedAt] = useState(null)
  // errors && success
  let [emptyFieldErrorMessage, setEmptyFieldErrorMessage] = useState(false)
  let [duplicateErrMessage, setDuplicateErrMessage] = useState(false)
  let [duplicateData, setDuplicateData] = useState(null)
  let [backendErrMessage, setBackendErrMessage] = useState(false)
  let [successMessage, setSuccessMessage] = useState(false)
  // delete mutation
  let [deleteModal, setDeleteModal] = useState(false)
  let [backDelErrMessage, setBackDelErrMessage] = useState(false)
  let [successDelMessage, setSuccessDelMessage] = useState(false)

  const history = useHistory()
  const goToCreateSensor = () => {
    history.push('/sensors/create')
  }

  // query sensors
  const [getSensors, { refetch, loading, called }] = useLazyQuery(SENSORS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      // set new set of sensors
      setSensors(data.me.sensors)
      // find if sensor exists in db & switch to edit mode
      let goToMode
      // already in route 'create' / skip other checks
      if (sensorId === 'create' || sensorId === undefined) return
      // empty route
      if (sensorId === '') goToMode = 'create'
      // edit mode
      let found = data.me.sensors.filter(sen => sen.id === sensorId)
      if (found && found.length > 0) {
        goToMode = 'edit'
        setMode('edit')
        let { created_at, license: lic, model: eModel, po_number, purchased_at, serial: eSerial } = found[0]
        setPo(po_number)
        setPurchased(moment(purchased_at).format('yyyy-MM-DD'))
        setLicense(lic)
        setModel(eModel)
        setSerial(eSerial)
        setCreatedAt(created_at)
      }
      if (!found || found.length === 0) goToMode = 'create'
      // setup url for  create mode
      if (goToMode === 'create') {
        setMode('create')
        goToCreateSensor()
      }
    }
  })
  // hide messages
  const hideErrorMessages = () => {
    setBackendErrMessage(false)
    setEmptyFieldErrorMessage(false)
  }
  // on submit form
  const handleSubmit = (event, b, c) => {
    event.preventDefault()
    if (po && po !== ''
      && purchased && purchased !== ''
      && serial && serial !== ''
      && model && model !== '') {
      // submit form
      let standardDate = moment(purchased).toISOString()
      if (mode === 'create') {
        createSensor({
          variables: {
            input: {
              po_number: po,
              purchased_at: standardDate,
              serial,
              model,
              license
            }
          }
        })
      } else if (mode === 'edit') {
        updateSensor({
          variables: {
            input: {
              id: sensorId,
              po_number: po,
              purchased_at: standardDate,
              serial,
              model,
              license: license || null
            }
          }
        })
      }
    } else {
      // empty fields detected
      setEmptyFieldErrorMessage(true)
    }
  }
  // on serial changed
  useEffect(() => {
    setDuplicateErrMessage(false)
    // check for serial duplicates
    let found = sensors.filter(sk => sk.serial === serial)
    // avoid race
    setTimeout(() => {
      if (found && found.length > 0) {
        setDuplicateErrMessage(true)
        setDuplicateData(found[0])
      }
    }, 50)
  }, [serial])
  // create sensor mutation
  const [createSensor, { data: createMutationData, loading: createMutationLoading, error: createMutationError }] = useMutation(CREATE_SENSOR, {
    fetchPolicy: 'network-only',
    onCompleted: createMutationData => {
      let addedSensor = createMutationData.create_sensor
      // backend error
      if (!addedSensor) setBackendErrMessage(true)
      // reset all fields
      setPo('')
      setPurchased('')
      setSerial('')
      setModel(sensorModels[0])
      setLicense('')
      setCreatedAt(null)
      // update sensors list
      getSensors()
      // set success message
      setSuccessMessage(true)
      setTimeout(() => {
        setSuccessMessage(false)
      }, 3000)
    },
    onError: createMutationError => {
      setBackendErrMessage(true)
      return console.log(createMutationError)
    }
  })
  // update sensor mutation
  const [updateSensor, { data: updateMutationData, loading: updateMutationLoading, error: updateMutationError }] = useMutation(UPDATE_SENSOR, {
    fetchPolicy: 'network-only',
    onCompleted: updateMutationData => {
      let addedSensor = updateMutationData.update_sensor
      // backend error
      if (!addedSensor) setBackendErrMessage(true)
      // update sensors list
      getSensors()
      // set success message
      setSuccessMessage(true)
      setTimeout(() => {
        setSuccessMessage(false)
      }, 3000)
    },
    onError: updateMutationError => {
      setBackendErrMessage(true)
      return console.log(updateMutationError)
    }
  })

  useEffect(() => {
    // first load of existing sensors
    getSensors()
    // cleanup on unload
    return () => {
      setPo('')
      setPurchased('')
      setSerial('')
      setModel(sensorModels[0])
      setLicense(null)
      setCreatedAt(null)
    }
  }, [])

  // check if its created before 24h ago
  const isNotOld = dateObj => {
    return ((moment().toDate() - moment(dateObj).toDate()) / (1000 * 60 * 60 * 24)) < 1
  }
  // delete sensor
  const goToSensorsPage = () => history.push('/sensors')
  const [deleteSensor] = useMutation(DELETE_SENSOR, {
    fetchPolicy: 'network-only',
    onCompleted: delData => {
      // hide comfirm modal
      setDeleteModal(false)
      let respData = delData.delete_sensor
      // backend error
      if (!respData) setBackDelErrMessage(true)
      // set success message
      setSuccessDelMessage(true)
      setTimeout(() => {
        setSuccessDelMessage(false)
        goToSensorsPage()
      }, 3000)
    },
    onError: delErr => {
      setDeleteModal(false)
      setBackDelErrMessage(true)
      setTimeout(() => {
        setBackDelErrMessage(false)
        goToSensorsPage()
      }, 3000)
      console.log(delErr)
    }
  })

  const handleDeleteSensor = e => {
    e.preventDefault()
    deleteSensor({
      variables: { input: { id: sensorId } }
    })
    setDeleteModal(false)
  }

  return (
    <>
      <Container className='UpsertSensor'>
        <Header as='h3' floated='left'>
          <Icon name='video camera' />
          <Header.Content>
            Sensor
            <Header.Subheader>
              {mode && mode === 'edit' ?
                (createdAt ? `Edit sensor added on: ${moment(createdAt).format('DD/MMM/YYYY')}` : (<>&emsp;</>)) :
                mode === 'create' && !loading && called ? 'Create new sensor:' : (<>&emsp;</>)
              }
            </Header.Subheader>
          </Header.Content>
        </Header>
        {mode && mode === 'edit' && createdAt && !!isNotOld(createdAt) && (
          <Header floated='right'>
            <Button
              negative
              onClick={() => setDeleteModal(true)}
            >
              <Icon name='trash alternate' /> DELETE
            </Button>
          </Header>
        )}
        {backDelErrMessage && (
          <Message
            negative
            size='small'
            className='delete-message'
            onDismiss={() => setBackDelErrMessage(false)}
          >
            <Message.Header>Failed to delete sensor!</Message.Header>
            <p>Backend refused to delete this sensor!</p>
          </Message>
        )}
        {successDelMessage && (
          <Message
            positive
            size='small'
            className='delete-message'
          >
            <Message.Header>Success!</Message.Header>
            <p>Sensor has been successfully deleted!</p>
          </Message>
        )}
        {loading && called && (<Loader active>Loading...</Loader>)}
        {called && !loading && (
          <Form onSubmit={handleSubmit} autoComplete='off'>
            <Form.Field className='model'>
              <Form.Input
                name='model'
                type='text'
                label='Model'
                required
                value={model}
                disabled
                onChange={e => {
                  setModel(e.target.value)
                  hideErrorMessages()
                }}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                name='po_number'
                type='text'
                label='PO Number'
                placeholder='Enter PO Number'
                required
                value={po}
                onChange={e => {
                  setPo(e.target.value)
                  hideErrorMessages()
                }}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                name='purchased_at'
                type='date'
                label='Purchased At'
                required
                value={purchased}
                onChange={e => {
                  setPurchased(e.target.value)
                  hideErrorMessages()
                }}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                name='serial'
                type='text'
                label='Serial'
                required
                value={serial}
                onChange={e => {
                  setSerial(e.target.value)
                  hideErrorMessages()
                }}
              />
            </Form.Field>
            {(duplicateErrMessage && duplicateData && mode === 'create') && (
              <Message
                negative
                size='mini'
              >
                <Message.Header style={{marginBottom: '0.5rem'}}>This sensor already exist in DataBase!</Message.Header>
                <Message.Content floated='right'>
                  <p>Serial: {(duplicateErrMessage && duplicateData) && duplicateData.serial}</p>
                  <p>Added on: {(duplicateErrMessage && duplicateData) && moment(duplicateData.created_at).format('DD/MMM/YYYY')}</p>
                </Message.Content>
              </Message>
            )}
            <Form.Field>
              <Form.Input
                name='license'
                type='text'
                label='License'
                value={license || ''}
                onChange={e => {
                  setLicense(e.target.value)
                  hideErrorMessages()
                }}
              />
            </Form.Field>
            {emptyFieldErrorMessage && (
              <Message
                negative
                size='mini'
              >
                <Message.Header>Empty Input Error!</Message.Header>
                <p>All required fields must be filed!</p>
              </Message>
            )}
            {backendErrMessage && (
              <Message
                negative
                size='small'
                onDismiss={() => setBackendErrMessage(false)}
              >
                <Message.Header>Failed to add sensor!</Message.Header>
                <p>Backend refused to create new sensor!</p>
              </Message>
            )}
            <div className='clear'>
              <Button
                type='submit'
                floated='right'
                primary
                content={mode === 'create' ? 'CREATE' : 'SUBMIT EDIT'}
                onClick={handleSubmit}
              />
            </div>
            {successMessage && (
              <Message
                positive
                size='small'
              >
                <Message.Header>Success!</Message.Header>
                <p>Sensor successfully {mode === 'create' ? 'added' : 'edited'}!</p>
              </Message>
            )}
          </Form>
        )}
      </Container>
      <Modal
        basic
        onClose={() => setDeleteModal(false)}
        onOpen={() => setDeleteModal(true)}
        open={deleteModal}
        size='small'
      >
        <Header icon>
          <Icon name='trash' />
          Are you sure you want to delete sensor?
        </Header>
        <Modal.Actions>
          <Button basic color='red' inverted onClick={() => setDeleteModal(false)}>
            <Icon name='remove' /> No
          </Button>
          <Button color='green' inverted onClick={handleDeleteSensor}>
            <Icon name='checkmark' /> Yes
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default UpsertSensor
